import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  AppBar, 
  Toolbar, 
  Button,
  TextField,
  Grid,
  ThemeProvider,
  CssBaseline,
  Paper,
  styled,
  Alert,
  CircularProgress,
  Snackbar,
  IconButton,
  Avatar,
  Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import theme from '../../styles/theme';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../components/Header/Logo';
import StatsCards from '../../components/Dashboard/StatsCards';
import TimeSeriesChart from '../../components/Dashboard/Charts/TimeSeriesChart';
import OwnerDistributionChart from '../../components/Dashboard/Charts/OwnerDistributionChart';
import { OpenSeaService, CollectionDetails } from '../../services/openSeaService';

const ContentContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginTop: theme.spacing(8),
}));

interface TimeSeriesDataset {
  label: string;
  data: number[];
  borderColor: string;
  backgroundColor: string;
}

interface TimeSeriesData {
  labels: string[];
  datasets: TimeSeriesDataset[];
}

interface OwnerDistributionDataset {
  data: number[];
  backgroundColor: string[];
  borderColor: string[];
  borderWidth: number;
}

interface OwnerDistributionData {
  labels: string[];
  datasets: OwnerDistributionDataset[];
}

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
  }
}));

const CollectionBanner = styled(Box)(({ theme }) => ({
  height: 240,
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  position: 'relative',
  marginBottom: theme.spacing(3),
}));

const BannerImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const CollectionAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `4px solid ${theme.palette.background.paper}`,
  position: 'absolute',
  bottom: -60,
  left: theme.spacing(3),
  boxShadow: theme.shadows[3],
}));

const PrivateContent: React.FC = () => {
  const { signOut, currentUser } = useAuth();
  const [collectionSlug, setCollectionSlug] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [collectionDetails, setCollectionDetails] = useState<CollectionDetails | null>(null);
  const [stats, setStats] = useState({
    totalVolume: 0,
    floorPrice: 0,
    bestOffer: 0,
    listedPercentage: 0,
    numOwners: 0,
    averagePriceChange: 0,
    salesVolumeChange: 0,
    liquidity: 0,
    uniqueBuyers: 0,
    uniqueSellers: 0
  });
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData>({
    labels: [],
    datasets: []
  });
  const [ownerDistribution, setOwnerDistribution] = useState<OwnerDistributionData>({
    labels: [],
    datasets: [{
      data: [],
      backgroundColor: [],
      borderColor: [],
      borderWidth: 1
    }]
  });
  
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Failed to sign out:', error);
    }
  };

  // Generate daily dates for the last month
  const generateDailyDates = (): string[] => {
    const dates: string[] = [];
    const today = new Date();
    for (let i = 30; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      dates.push(date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }));
    }
    return dates;
  };

  const fetchCollectionData = async () => {
    if (!collectionSlug) {
      setError('Пожалуйста, введите slug коллекции');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Fetch collection details, stats, and events
      const details = await OpenSeaService.getCollectionDetails(collectionSlug);
      const statsData = await OpenSeaService.getCollectionStats(collectionSlug);
      const bestListings = await OpenSeaService.getBestListings(collectionSlug);
      const allListings = await OpenSeaService.getAllListings(collectionSlug);
      const events = await OpenSeaService.getCollectionEvents(collectionSlug);

      // Set collection details
      setCollectionDetails(details);

      // Calculate additional metrics
      const additionalMetrics = OpenSeaService.calculateAdditionalMetrics(statsData, events);

      // Update stats
      setStats({
        totalVolume: statsData.total.volume,
        floorPrice: statsData.total.floor_price,
        bestOffer: bestListings[0]?.price.current.value ? parseFloat(bestListings[0].price.current.value) : 0,
        listedPercentage: allListings.length > 0 ? (allListings.length / (details.total_supply || 10000)) * 100 : 0,
        numOwners: statsData.total.num_owners,
        averagePriceChange: additionalMetrics.averagePriceChange,
        salesVolumeChange: additionalMetrics.salesVolumeChange,
        liquidity: additionalMetrics.liquidity,
        uniqueBuyers: additionalMetrics.uniqueBuyers,
        uniqueSellers: additionalMetrics.uniqueSellers
      });

      // Generate daily dates for the last month
      const dates = generateDailyDates();
      
      // Process time series data - we'll use the intervals from the API
      // but extend it to show daily data points (note: actual data might have fewer points)
      const volumeData = new Array(dates.length).fill(0);
      const floorPriceData = new Array(dates.length).fill(statsData.total.floor_price);
      
      // Fill in available data from the API
      statsData.intervals.forEach((interval: any, index: number) => {
        // Only use as many points as we have in our API response
        if (index < dates.length) {
          volumeData[dates.length - 1 - index] = interval.volume;
        }
      });

      const processedTimeSeriesData: TimeSeriesData = {
        labels: dates,
        datasets: [
          {
            label: 'Volume',
            data: volumeData,
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.main + '20',
          },
          {
            label: 'Floor Price',
            data: floorPriceData,
            borderColor: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.main + '20',
          }
        ]
      };
      setTimeSeriesData(processedTimeSeriesData);

      // Prepare owner distribution data
      // Since OpenSea API doesn't provide detailed owner distribution,
      // we'll create reasonable segments based on numOwners
      if (statsData.total.num_owners > 0) {
        // Typical NFT collection distribution percentages
        const oneItemPercent = 60;
        const twoToThreePercent = 20;
        const fourToTenPercent = 15;
        const tenPlusPercent = 5;
        
        const oneItemCount = Math.round(statsData.total.num_owners * oneItemPercent / 100);
        const twoToThreeCount = Math.round(statsData.total.num_owners * twoToThreePercent / 100);
        const fourToTenCount = Math.round(statsData.total.num_owners * fourToTenPercent / 100);
        const tenPlusCount = Math.round(statsData.total.num_owners * tenPlusPercent / 100);
        
        const ownerDistData: OwnerDistributionData = {
          labels: ['1 NFT', '2-3 NFT', '4-10 NFT', '10+ NFT'],
          datasets: [{
            data: [oneItemCount, twoToThreeCount, fourToTenCount, tenPlusCount],
            backgroundColor: [
              theme.palette.primary.main,
              theme.palette.primary.light,
              theme.palette.secondary.main,
              theme.palette.secondary.light,
            ],
            borderColor: [
              theme.palette.primary.dark,
              theme.palette.primary.main,
              theme.palette.secondary.dark,
              theme.palette.secondary.main,
            ],
            borderWidth: 1
          }]
        };
        setOwnerDistribution(ownerDistData);
      }

      setLastUpdated(new Date());
      setSnackbarOpen(true);

    } catch (error) {
      console.error('Error fetching collection data:', error);
      setError('Не удалось получить данные коллекции. Проверьте slug коллекции и попробуйте снова.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ flexGrow: 1 }}>
        <AppBar 
          position="fixed" 
          color="default" 
          elevation={1}
          sx={{ 
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)'
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ justifyContent: 'space-between' }}>
              <Logo />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {currentUser && (
                  <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
                    {currentUser.email}
                  </Typography>
                )}
                <Button 
                  color="primary" 
                  variant="outlined" 
                  onClick={handleSignOut}
                >
                  Выйти
                </Button>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
        
        <ContentContainer>
          <Container maxWidth="lg">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StyledPaper>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Typography variant="h5" fontWeight="700" gutterBottom>
                        Аналитика NFT коллекций
                      </Typography>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Введите slug коллекции из OpenSea для просмотра аналитики
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                          fullWidth
                          label="Slug коллекции"
                          value={collectionSlug}
                          onChange={(e) => setCollectionSlug(e.target.value)}
                          placeholder="например, azuki"
                          helperText="Введите slug коллекции из URL OpenSea после '/collection/'"
                          InputProps={{
                            endAdornment: (
                              <IconButton 
                                size="small" 
                                title="Slug коллекции - это текст, который появляется в URL OpenSea после '/collection/'"
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            )
                          }}
                        />
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={fetchCollectionData}
                          disabled={isLoading}
                          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                          sx={{ height: 56, minWidth: 120, px: 3 }}
                        >
                          {isLoading ? 'Загрузка...' : 'Получить данные'}
                        </Button>
                      </Box>
                      {lastUpdated && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Последнее обновление: {lastUpdated.toLocaleString('ru-RU')}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </StyledPaper>
              </Grid>

              {error && (
                <Grid item xs={12}>
                  <Alert 
                    severity="error" 
                    action={
                      <IconButton
                        color="inherit"
                        size="small"
                        onClick={() => setError(null)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    {error}
                  </Alert>
                </Grid>
              )}

              {collectionDetails && (
                <Grid item xs={12}>
                  <CollectionBanner>
                    <BannerImage 
                      src={collectionDetails.banner_image_url || 'https://via.placeholder.com/1200x240?text=No+Banner'} 
                      alt={`${collectionDetails.name} banner`}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.src = 'https://via.placeholder.com/1200x240?text=Error+Loading+Banner';
                      }}  
                    />
                    <CollectionAvatar 
                      src={collectionDetails.image_url || 'https://via.placeholder.com/120?text=No+Image'} 
                      alt={collectionDetails.name}
                      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                        e.currentTarget.src = 'https://via.placeholder.com/120?text=Error';
                      }}
                    />
                  </CollectionBanner>
                  <Box sx={{ pl: 2, mb: 3, mt: 5 }}>
                    <Typography variant="h4" fontWeight="700">
                      {collectionDetails.name}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 3, maxWidth: '80%' }}>
                      {collectionDetails.description || 'Нет описания'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Общее количество токенов
                        </Typography>
                        <Typography variant="h6">
                          {collectionDetails.total_supply?.toLocaleString() || 'N/A'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Дата создания
                        </Typography>
                        <Typography variant="h6">
                          {collectionDetails.created_date 
                            ? new Date(collectionDetails.created_date).toLocaleDateString('ru-RU') 
                            : 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 3 }} />
                </Grid>
              )}

              <Grid item xs={12}>
                <StatsCards stats={stats} isLoading={isLoading} />
              </Grid>

              <Grid item xs={12}>
                <TimeSeriesChart 
                  title="Объем и минимальная цена (30 дней)"
                  data={timeSeriesData}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TimeSeriesChart 
                  title="История минимальной цены (30 дней)"
                  data={{
                    labels: timeSeriesData.labels,
                    datasets: timeSeriesData.datasets.length > 1 ? [timeSeriesData.datasets[1]] : []
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <OwnerDistributionChart 
                  data={ownerDistribution}
                  totalOwners={stats.numOwners}
                />
              </Grid>
            </Grid>
          </Container>
        </ContentContainer>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message="Данные успешно обновлены"
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleCloseSnackbar}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </ThemeProvider>
  );
};

export default PrivateContent; 